import 'react-datepicker/dist/react-datepicker.css';
import { ValueEditorProps } from 'react-querybuilder';
import { match } from 'ts-pattern';

import { ChakraValueEditor } from '@react-querybuilder/chakra';

import { PropertyTypeEnum, QueryBuilderOperator } from '@dotfile/shared/domain';

import { Box } from '../../layout/box/box';
import { Text } from '../../typography/text';
import { YesNoButton } from '../radio-button';
import {
  QueryBuilderFieldValueSelectorComponent,
  TypedQueryBuilderField,
} from './type';
import { useValidation } from './use-validation';
import { ValueEditorCompanyClassifications } from './value-editor-company-classifications';
import { ValueEditorCountry } from './value-editor-countries';
import { ValueEditorDate } from './value-editor-date';
import { ValueEditorEntityLegalForm } from './value-editor-entity-legal-form';
import { ValueEditorNumeric } from './value-editor-numeric';
import { ValueSelector } from './value-selector';

export const ValueEditor = (
  props: ValueEditorProps<TypedQueryBuilderField, QueryBuilderOperator>,
): JSX.Element | null => {
  const { isInvalid, errorMessage } = useValidation(props.validation);

  if (['null', 'notNull'].includes(props.operator)) {
    // No value selector for null/notNull so returns a simple box as a place holder
    return <Box />;
  }

  const multiple = [
    'in',
    'notIn',
    'arrayContains',
    'arrayContainsAll',
    'arrayContainsExactly',
    'doesNotArrayContain',
    'doesNotArrayContainAll',
  ].includes(props.operator);

  return (
    <Box position="relative">
      {match(props.fieldData)
        .when(
          (
            fieldData,
          ): fieldData is typeof fieldData & {
            component: QueryBuilderFieldValueSelectorComponent;
          } => !!fieldData.component,
          (fieldData) => (
            <fieldData.component
              {...props}
              multiple={multiple}
              fieldData={fieldData}
              isInvalid={isInvalid}
            />
          ),
        )
        .when(isFieldDataType(PropertyTypeEnum.boolean), () => (
          <YesNoButton
            onChange={props.handleOnChange}
            defaultValue={props.value}
            isReadOnly={props.disabled}
          />
        ))
        .when(isFieldDataType(PropertyTypeEnum.choices), () => (
          <ValueSelector
            {...props}
            multiple={multiple}
            options={props.values ?? []}
            isInvalid={isInvalid}
          />
        ))
        .when(
          isFieldDataType(PropertyTypeEnum.classifications),
          (fieldData) => (
            <ValueEditorCompanyClassifications
              {...props}
              multiple={multiple}
              fieldData={fieldData}
              isInvalid={isInvalid}
            />
          ),
        )
        .when(isFieldDataType(PropertyTypeEnum.countries), (fieldData) => (
          <ValueEditorCountry
            {...props}
            multiple={multiple}
            fieldData={fieldData}
            isInvalid={isInvalid}
          />
        ))
        .when(
          isFieldDataType(PropertyTypeEnum.entity_legal_form),
          (fieldData) => (
            <ValueEditorEntityLegalForm
              {...props}
              multiple={multiple}
              fieldData={fieldData}
              isInvalid={isInvalid}
            />
          ),
        )
        .when(isFieldDataType(PropertyTypeEnum.date), (fieldData) => (
          <ValueEditorDate
            {...props}
            fieldData={fieldData}
            isInvalid={isInvalid}
          />
        ))
        .when(isFieldDataType(PropertyTypeEnum.numeric), (fieldData) => (
          <ValueEditorNumeric
            {...props}
            fieldData={fieldData}
            isInvalid={isInvalid}
          />
        ))
        .otherwise(() => (
          <ChakraValueEditor
            {...(props as unknown as ValueEditorProps)}
            extraProps={{
              backgroundColor: isInvalid
                ? 'var(--chakra-colors-red-50)'
                : 'white',
              borderColor: isInvalid
                ? 'var(--chakra-colors-red-500)'
                : 'var(--chakra-colors-gray-300)',
            }}
          />
        ))}
      {!!errorMessage && (
        <Text
          fontSize="sm"
          color="red.500"
          position="absolute"
          bottom="-5"
          left={0}
        >
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};

const isFieldDataType =
  <TPropertyType extends PropertyTypeEnum>(type: TPropertyType) =>
  (
    fieldData: TypedQueryBuilderField,
  ): fieldData is TypedQueryBuilderField<TPropertyType> =>
    fieldData.type === type;
